import { useMemo, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatAmount, formatRef, parseRef } from '@promanage/core';
import { costPerEnquiry, type ListingBoardStatus } from '@promanage/core/listing';
import { Amount, Button } from '@promanage/ui';
import { ColumnPicker } from '../features/table/ColumnPicker';
import { DataTable } from '../features/table/DataTable';
import { EmptyState, FilterChips, ListShell } from '../features/table/ListShell';
import { compareRows, type SortState } from '../features/table/columns';
import { useColumnState } from '../features/table/useColumnState';
import { PropertiesNav } from '../features/property/PropertiesNav';
import {
  DEFAULT_LISTING_SORT,
  LISTING_COLUMNS,
  type ListingColumnId,
} from '../features/listings/listingColumns';
import type { ListingBoardRow } from '../features/listings/types';
import { usePortfolioScope } from '../features/portfolio/scope';
import { WorkspaceMenu, useWorkspace } from '../features/workspaces/WorkspaceMenu';
import { workspaceChoice, workspaceTable, workspaceText } from '../features/workspaces/state';
import { trpc, type RouterOutputs } from '../lib/trpc';

export const Route = createFileRoute('/properties/listings')({
  component: ListingsPage,
});

const STORAGE_KEY = 'promanage:listing-columns';

type ListingFilter = 'all' | 'market' | 'needs' | 'drafts';

const FILTERS: { value: ListingFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'market', label: 'On the market' },
  { value: 'needs', label: 'Needs listing' },
  { value: 'drafts', label: 'Not listed' },
];

const IN_FILTER: Record<Exclude<ListingFilter, 'all'>, readonly ListingBoardStatus[]> = {
  market: ['unresolved', 'lapsed', 'expiring', 'listed'],
  needs: ['vacant', 'ending'],
  drafts: ['draft'],
};

type Board = RouterOutputs['listings']['board'];
type Stats = RouterOutputs['listings']['tradeMeStats'];

/** One identity, so the pending state does not re-sort the table on every render. */
const NO_ROWS: Board['rows'] = [];

/**
 * The listings board — every property on the market, and every one that ought to be.
 *
 * ── WHY THIS IS NOT THE PROPERTY LIST WITH A FILTER ──────────────────────────────────────────
 *
 * The property list is the whole book. This is a queue with money on it: a vacant house costs its
 * owner a week's rent every seven days, and the agency finds out which ones it has let slip only by
 * opening each property's Listing tab. So the default order is URGENCY, not address — a placement
 * that might have been charged twice, then an advertisement nobody can see because Trade Me's 21
 * days ran out, then a vacancy nobody has advertised at all.
 *
 * ── THE ADVERTISEMENT IS STILL CHANGED IN ONE PLACE ──────────────────────────────────────────
 *
 * Every row opens the property's Listing tab. Writing the copy, quoting the fee, placing,
 * updating and withdrawing all stay there, behind the fee confirmation and the double-charge guard
 * that took three migrations to get right. A second set of those buttons here would be a second
 * place for that to go wrong, for the saving of one click.
 *
 * ── TWO QUERIES, AND ONLY ONE OF THEM WAITS ──────────────────────────────────────────────────
 *
 * The board is the agency's own record and draws at once. Trade Me's views and enquiries arrive
 * behind it from a cached server read, and fill blank columns in when they land; if Trade Me is
 * down, the board is still the board.
 */
function ListingsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { scope, setScope, canToggle } = usePortfolioScope();

  const board = useQuery(
    trpc.listings.board.queryOptions(
      { scope },
      { meta: { errorTitle: 'Could not load the listings' }, staleTime: 30_000 },
    ),
  );

  const anyLive = (board.data?.rows ?? NO_ROWS).some((row) => row.tradeMe?.status === 'live');
  const statsOptions = trpc.listings.tradeMeStats.queryOptions(undefined, {
    enabled: board.data?.tradeMeAvailable === true && anyLive,
    meta: { errorTitle: 'Could not read views and enquiries from Trade Me' },
    // The server holds each read for ten minutes; asking it more often than that buys nothing.
    staleTime: 5 * 60_000,
    retry: false,
  });
  const stats = useQuery(statsOptions);

  /*
   * A refresh is a separate call with `refresh: true`, and its answer is written over the ordinary
   * query's cache — so the table reads one place whichever way the numbers arrived.
   */
  const refresh = useMutation({
    mutationFn: () =>
      queryClient.fetchQuery(
        trpc.listings.tradeMeStats.queryOptions({ refresh: true }, { staleTime: 0 }),
      ),
    meta: { errorTitle: 'Could not refresh the figures from Trade Me' },
    onSuccess: (data: Stats) => queryClient.setQueryData(statsOptions.queryKey, data),
  });

  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<ListingFilter>('all');
  const [sort, setSort] = useState<SortState<ListingColumnId>>(DEFAULT_LISTING_SORT);
  const columns = useColumnState(STORAGE_KEY, LISTING_COLUMNS);
  const workspace = useWorkspace({
    route: '/properties/listings',
    state: { filters: { query, filter, scope }, columns: columns.state.order, sort },
    apply(state) {
      const table = workspaceTable(state, LISTING_COLUMNS, DEFAULT_LISTING_SORT);
      setQuery(workspaceText(state, 'query'));
      setFilter(
        workspaceChoice(
          state,
          'filter',
          FILTERS.map((option) => option.value),
          'all',
        ),
      );
      if (canToggle) setScope(workspaceChoice(state, 'scope', ['own', 'agency'], 'own'));
      columns.setState(table.columns);
      setSort(table.sort);
    },
  });

  const rows = useMemo(() => {
    const reach = stats.data?.stats ?? {};
    const needle = query.trim().toLowerCase();
    const asRef = needle === '' ? null : parseRef(needle);

    return (board.data?.rows ?? NO_ROWS)
      .map((row): ListingBoardRow => {
        const found =
          row.tradeMe?.status === 'live' ? (reach[row.tradeMe.externalListingId] ?? null) : null;
        return {
          ...row,
          reach: found,
          costPerEnquiryCents:
            found?.enquiries !== null &&
            found?.enquiries !== undefined &&
            row.campaignFeesCents !== null
              ? costPerEnquiry(BigInt(row.campaignFeesCents), found.enquiries)
              : null,
        };
      })
      .filter((row) => filter === 'all' || IN_FILTER[filter].includes(row.status))
      .filter((row) => {
        if (needle === '') return true;
        if (asRef) return asRef.entity === 'property' && asRef.sequence === row.refSeq;
        return [
          row.address,
          row.suburb,
          row.title,
          formatRef('property', row.refSeq),
          row.tradeMe?.externalListingId,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
          .includes(needle);
      })
      .sort((a, b) => compareRows(a, b, sort, LISTING_COLUMNS));
  }, [board.data, stats.data, query, filter, sort]);

  const counts = board.data?.counts;
  const onMarket = counts
    ? counts.listed + counts.expiring + counts.lapsed + counts.unresolved
    : 0;
  const summary = !counts
    ? 'Loading…'
    : [
        `${onMarket} on the market`,
        counts.vacant > 0 ? `${counts.vacant} vacant and not listed` : null,
        counts.ending > 0 ? `${counts.ending} with a tenancy ending` : null,
        counts.lapsed > 0 ? `${counts.lapsed} expired on Trade Me` : null,
      ]
        .filter(Boolean)
        .join(' · ');

  return (
    <ListShell
      title="Listings"
      subtitle={summary}
      nav={<PropertiesNav />}
      meta={board.data ? <BoardFigures board={board.data} /> : null}
      actions={
        <>
          <WorkspaceMenu workspace={workspace} />
          <ColumnPicker
            columns={LISTING_COLUMNS}
            state={columns.state}
            onChange={columns.setState}
            onReset={columns.reset}
          />
        </>
      }
      search={query}
      onSearchChange={setQuery}
      searchPlaceholder="Address, suburb, ref or Trade Me number"
      searchLabel="Search listings"
      filters={
        <FilterChips label="Show" value={filter} options={FILTERS} onChange={setFilter} />
      }
      trailing={
        anyLive && board.data?.tradeMeAvailable ? (
          <ReachStatus
            fetchedAt={stats.data?.fetchedAt ?? null}
            loading={stats.isFetching || refresh.isPending}
            onRefresh={() => refresh.mutate()}
          />
        ) : null
      }
      isPending={board.isPending}
      error={board.error}
      onRetry={() => void board.refetch()}
      isEmpty={rows.length === 0}
      empty={
        <EmptyState title={query || filter !== 'all' ? 'Nothing matches' : 'Nothing to list'}>
          {query || filter !== 'all'
            ? 'Try another filter, or a different address, ref or Trade Me number.'
            : 'Every property is let with no end date set, and none is on the market. A property appears here when it falls vacant, when its tenancy is given an end date, or when an advertisement is written for it.'}
        </EmptyState>
      }
    >
      <DataTable
        rows={rows}
        columns={columns.visible}
        sort={sort}
        onSortChange={setSort}
        rowKey={(row) => row.propertyId}
        onOpen={(row) =>
          void navigate({
            to: '/properties/$ref',
            params: { ref: formatRef('property', row.refSeq) },
            search: { tab: 'listing' },
          })
        }
        caption={`Listings, one row per property on or due for the market. ${summary}.`}
        minWidth="80rem"
      />
    </ListShell>
  );
}

/**
 * The two figures that say how the agency is doing at letting, rather than what is on the list.
 *
 * MEDIAN DAYS ON MARKET over campaigns that ended in the last ninety days, and FEES over thirty —
 * the portal spend a principal asks about, and the one that moves when somebody relists instead
 * of re-pricing.
 */
function BoardFigures({ board }: { board: Board }) {
  return (
    <div className="flex gap-5 leading-tight whitespace-nowrap">
      <div>
        <span className="block text-[0.6875rem] text-ink-subtle">
          Median days on market · 90 days
        </span>
        <span className="text-[0.9375rem] font-semibold tabular-nums">
          {board.medianDaysOnMarket === null ? (
            <span className="font-medium text-ink-subtle">—</span>
          ) : (
            `${board.medianDaysOnMarket} ${board.medianDaysOnMarket === 1 ? 'day' : 'days'}`
          )}
        </span>
      </div>
      <div>
        <span className="block text-[0.6875rem] text-ink-subtle">Trade Me fees · 30 days</span>
        <Amount
          value={BigInt(board.feesLast30DaysCents)}
          currency
          className="text-[0.9375rem] font-semibold"
          title={`Last 12 months: $${formatAmount(BigInt(board.feesLast365DaysCents))}`}
        />
      </div>
    </div>
  );
}

function ReachStatus({
  fetchedAt,
  loading,
  onRefresh,
}: {
  fetchedAt: string | null;
  loading: boolean;
  onRefresh: () => void;
}) {
  return (
    <div className="flex items-center gap-2 text-[0.75rem] text-ink-subtle">
      <span>
        {loading
          ? 'Asking Trade Me…'
          : fetchedAt
            ? `Trade Me figures as at ${new Date(fetchedAt).toLocaleTimeString('en-NZ', {
                hour: 'numeric',
                minute: '2-digit',
                timeZone: 'Pacific/Auckland',
              })}`
            : 'Trade Me figures not loaded'}
      </span>
      <Button size="sm" variant="secondary" disabled={loading} onClick={onRefresh}>
        Refresh
      </Button>
    </div>
  );
}
